import React, { useEffect, useMemo, useState } from "react";
import { Segment, Select, Button, Checkbox, Message } from "semantic-ui-react";
import { connect } from "react-redux";
import format from "date-fns/format";
import Header from "../../assets/components/header";
import Footer from "../../assets/components/footer";
import NumberInput from "../../assets/components/NumberInput";
import TeeTimeBooking from "../../models/TeeTimeBooking";
import CourseVoucher from "../../models/CourseVoucher";
import Member from "../../models/Member";
import GolfClub from "../../models/GolfClub";
import GolfCourse from "../../models/GolfCourse";
import MemberSearchSelect from "../../assets/components/MemberSearchSelect";
import GolfClubSearchSelect from "../../assets/components/GolfClubSearchSelect";
import CourseAvailabilityCalendar from "../../assets/components/golfClub/CourseAvailabilityCalendar";
import MemberChip from "../../assets/components/MemberChip";

const pad2 = (n) => String(n).padStart(2, "0");
const formatTimeslotHour = (hour) => `${pad2(hour)}:00 - ${pad2(hour)}:59`;

/**
 * Shared UI blocks
 */
const MemberBlock = ({ member, bookingType, onChangeMember }) => {
    return (
        <Segment vertical>
            <label className='with-input'>Member</label>
            <MemberSearchSelect
                onChange={(value) => onChangeMember(value)}
                value={member?._id ?? null}
                hasUsableVoucher={bookingType === "VOUCHER"}
                active
            />
            {member?._id && (
                <MemberChip
                    member={{
                        _id: member._id,
                        name: "Hover for member information",
                    }}
                    hideMembership
                    hideProfileImage
                    disableLink
                    className='ml-3'
                />
            )}
            {bookingType === "CREDIT" &&
                member?.canAccessTeeTimeCredit === false && (
                    <span className='ml-3'>
                        <b>
                            The selected member does not have access to Green
                            Fee Credit
                        </b>
                    </span>
                )}
        </Segment>
    );
};

const PlayersNotice = ({
    bookingType,
    isCreditBooking,
    voucher,
    playersMin,
    playersMax,
}) => {
    // Voucher bookings: fixed players
    if (bookingType === "VOUCHER" && voucher !== null) {
        return (
            <div className='ml-3 mt-2'>
                <div>
                    This booking is using a voucher, so the number of players is
                    fixed.
                </div>
                <div>
                    Voucher players: <b>{playersMin}</b>
                </div>
            </div>
        );
    }

    // Credit bookings
    if (isCreditBooking === true) {
        return (
            <div className='ml-3 mt-2'>
                <div>
                    Green Fee Credit bookings at this club are restricted to the
                    following player range:
                </div>
                <div>
                    Permitted players: <b>{playersMin}</b> – <b>{playersMax}</b>
                </div>
            </div>
        );
    }

    // Cash bookings
    return (
        <div className='ml-3 mt-2'>
            <div>
                Cash bookings at this club are restricted to the following
                player range:
            </div>
            <div>
                Permitted players: <b>{playersMin}</b> – <b>{playersMax}</b>
            </div>
        </div>
    );
};

/**
 * CASH FLOW
 * - Member, Club (+ filter toggle), Course, Players, Calendar (not credit, no voucher), Save
 * - Timeslot stored as numeric hour (derived from calendar time string today)
 * - Booking price block is stubbed here because create.js does not yet have a quote call wired
 */
const CashFlow = ({ history, member }) => {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState(null);

    const [club, setClub] = useState(null);
    const [course, setCourse] = useState(null);
    const [clubCourses, setClubCourses] = useState([]);
    const [memberAccessClubsFilter, setMemberAccessClubsFilter] =
        useState(true);

    const [clubRetreived, setClubRetreived] = useState(null);

    const cashMin = clubRetreived?.bookingCashMinPlayers ?? 1;
    const cashMax = clubRetreived?.bookingCashMaxPlayers ?? 100;

    const [players, setPlayers] = useState(4);
    const [playersDraft, setPlayersDraft] = useState(String(4));

    const playersMin = cashMin;
    const playersMax = cashMax;

    const [overrideAvailability, setOverrideAvailability] = useState(false);
    const [hasSelectedDate, setHasSelectedDate] = useState(false);
    const [hasSelectedTime, setHasSelectedTime] = useState(false);

    const clampPlayers = (nextMin, nextMax) => {
        setPlayers((p) => {
            const v = p ?? nextMin;
            const clamped = Math.min(nextMax, Math.max(nextMin, v));
            setPlayersDraft(String(clamped));
            return clamped;
        });
    };

    const commitPlayersDraft = () => {
        const n = parseInt(playersDraft, 10);
        if (Number.isNaN(n)) {
            setPlayersDraft(String(playersMin));
            setPlayers(playersMin);
            return;
        }
        const clamped = Math.min(playersMax, Math.max(playersMin, n));
        setPlayers(clamped);
        setPlayersDraft(String(clamped));
    };

    const effectivePlayers = useMemo(() => {
        const n = parseInt(playersDraft, 10);
        if (Number.isNaN(n)) return null;
        const clamped = Math.min(playersMax, Math.max(playersMin, n));
        // Only treat it as valid if the user has actually entered something numeric
        return clamped;
    }, [playersDraft, playersMin, playersMax]);

    const [selectedDate, setSelectedDate] = useState(new Date());

    // We keep the calendar timeValue as a string for now (current calendar expects it).
    // We ALSO store the numeric hour (required by your new spec).
    const [timeslotHour, setTimeslotHour] = useState(null);

    const onChangeDate = (date) => {
        date.setHours(12);
        setSelectedDate(date);
        setHasSelectedDate(true);
    };

    const onChangeTime = (hour) => {
        setTimeslotHour(hour);

        if (hour === null) {
            setHasSelectedTime(false);
        } else {
            setHasSelectedTime(true);
        }
    };

    const getClubCourses = async (clubId) => {
        const clubCourses = await new GolfClub({ _id: clubId }).coursesIndex({
            sort: "name",
        });

        setClubCourses(clubCourses);

        if (clubCourses.length > 0) {
            setCourse(clubCourses[0]);
        } else {
            setCourse(null);
        }
    };

    const onChangeClub = async (value, retreived) => {
        setClubRetreived(retreived ?? null);
        setClub(value);
        await getClubCourses(value);

        const nextMin = retreived?.bookingCashMinPlayers ?? 1;
        const nextMax = retreived?.bookingCashMaxPlayers ?? 100;
        clampPlayers(nextMin, nextMax);
    };

    const onChangeCourse = (value) => {
        const selected = clubCourses.find(({ _id }) => _id === value);
        setCourse(selected ?? null);
    };

    // Price info block (placeholder)
    // You said this will come from a quote endpoint and depends on selected date/time.
    // This file is ready for it. Wire it once the quote model method exists.
    const [quote, setQuote] = useState(null);
    const currencySymbol = quote?.currency?.symbol ?? "£";

    const [cashPrice, setCashPrice] = useState(0); // manual when overrideAvailability is true

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!member?._id || !course?._id) {
                setQuote(null);
                return;
            }

            // If override is on, we intentionally do not calculate pricing
            if (overrideAvailability === true) {
                setQuote(null);
                return;
            }

            if (hasSelectedDate === false || hasSelectedTime === false) {
                setQuote(null);
                return;
            }

            const day = selectedDate.getDate();
            const month = selectedDate.getMonth() + 1;
            const year = selectedDate.getFullYear();
            const hour = timeslotHour;

            if (effectivePlayers == null) {
                setQuote(null);
                return;
            }

            try {
                const q = await GolfCourse.quote(
                    course._id,
                    day,
                    month,
                    year,
                    hour,
                    effectivePlayers,
                    member._id,
                    null, // voucher
                    null, // booking
                );

                if (!cancelled) setQuote(q);
            } catch (e) {
                if (!cancelled) setQuote(null);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [
        member?._id,
        course?._id,
        selectedDate,
        timeslotHour,
        effectivePlayers,
        overrideAvailability,
        hasSelectedDate,
        hasSelectedTime,
    ]);

    const save = async () => {
        setSaving(true);
        setErrors(null);

        const finalPlayers = effectivePlayers ?? playersMin;

        try {
            const booking = new TeeTimeBooking({
                user: member._id,
                choices: [{ course: course._id }],
                players: finalPlayers,
                date: selectedDate,
                isCreditBooking: false,
                timeslot: formatTimeslotHour(timeslotHour),
            });

            if (overrideAvailability === true) {
                booking.price = cashPrice;
            }

            const response = await booking.store();
            history.push("/tee-time-requests/" + response._id);
        } catch (e) {
            const apiErrors = e?.response?.data?.errors ?? {
                general: ["Something went wrong saving this booking."],
            };

            setErrors(apiErrors);
        } finally {
            setSaving(false);
        }
    };

    const hasValidSelectedDate =
        selectedDate instanceof Date &&
        !Number.isNaN(selectedDate?.getTime?.());

    const hasValidTimeslot =
        timeslotHour !== null &&
        (overrideAvailability === true
            ? hasValidSelectedDate
            : hasSelectedDate === true && hasSelectedTime === true);

    const needsManualCost = overrideAvailability === true;
    const manualCostInvalid = needsManualCost && (!cashPrice || cashPrice <= 0);

    const canSave =
        hasValidTimeslot && (needsManualCost ? !manualCostInvalid : true);

    return (
        <>
            <Segment vertical>
                <label className='with-input'>Club</label>
                <GolfClubSearchSelect
                    value={club ?? null}
                    onChange={onChangeClub}
                    memberCanAccess={
                        memberAccessClubsFilter ? member._id : null
                    }
                    acceptsCredit={false}
                    retrieve={[
                        "bookingCashMinPlayers",
                        "bookingCashMaxPlayers",
                        "bookingCreditMinPlayers",
                        "bookingCreditMaxPlayers",
                    ]}
                />
                <Checkbox
                    toggle
                    label='Hide clubs the member cannot access'
                    checked={memberAccessClubsFilter}
                    className='ml-3'
                    onChange={() => {
                        setMemberAccessClubsFilter(
                            memberAccessClubsFilter === false,
                        );
                        setClub(null);
                        setCourse(null);
                        setClubRetreived(null);
                    }}
                />
            </Segment>

            {club && (
                <Segment vertical>
                    <label className='with-input'>Course</label>
                    <Select
                        search
                        value={course?._id ?? null}
                        onChange={(_, { value }) => onChangeCourse(value)}
                        options={clubCourses.map((c, index) => ({
                            key: index,
                            text: c.name, // ✅ course name only
                            value: c._id,
                        }))}
                    />
                </Segment>
            )}

            {course && (
                <>
                    <Segment vertical>
                        <label className='with-input'>No. of players</label>

                        <NumberInput
                            min={playersMin}
                            max={playersMax}
                            placeholder='No. of players'
                            value={playersDraft}
                            onChange={(value) => {
                                const v =
                                    value === null ||
                                    typeof value === "undefined"
                                        ? ""
                                        : String(value);
                                setPlayersDraft(v.replace(/[^\d]/g, ""));
                            }}
                            onBlur={commitPlayersDraft}
                        />

                        <PlayersNotice
                            bookingType='CASH'
                            isCreditBooking={false}
                            voucher={null}
                            playersMin={playersMin}
                            playersMax={playersMax}
                        />
                    </Segment>

                    <Segment vertical>
                        <label className='with-input'>Date & time</label>

                        {/* NOTE: CourseAvailabilityCalendar currently manages blocking + override internally.
                           It still emits a string timeslot. We store the numeric hour from it above. */}
                        <CourseAvailabilityCalendar
                            dateValue={selectedDate}
                            timeValue={timeslotHour}
                            member={member._id}
                            course={course._id}
                            voucher={null}
                            onChangeDate={onChangeDate}
                            onChangeTime={onChangeTime}
                            onOverrideChange={(next) => {
                                setOverrideAvailability(next);

                                // When override is toggled ON: force user to pick date + time again
                                if (next === true) {
                                    const d = new Date();
                                    d.setHours(12);
                                    setSelectedDate(d);

                                    setTimeslotHour(null);
                                    setHasSelectedDate(false);
                                    setHasSelectedTime(false);

                                    setQuote(null);
                                    setCashPrice(0);
                                }
                            }}
                            isCreditBooking={false}
                            showTimeslotSelection
                            defaultFirstAvailable
                        />
                    </Segment>

                    {needsManualCost === true || quote ? (
                        <Segment vertical>
                            <label className='with-input'>
                                Override Booking Price
                            </label>

                            {needsManualCost ? (
                                <>
                                    <p>
                                        As you are overriding the date and time
                                        availability for this course, we are
                                        unable to calculate the pricing for this
                                        booking. Please enter the total price
                                        the member will need to pay into the
                                        box.
                                    </p>

                                    <label className='with-input'>
                                        Booking Cost Total
                                    </label>
                                    <NumberInput
                                        min={0}
                                        placeholder='Booking Cost'
                                        value={cashPrice}
                                        onChange={(value) =>
                                            setCashPrice(value)
                                        }
                                    />

                                    {manualCostInvalid && (
                                        <p className='mt-2'>
                                            <b>
                                                You must enter a valid Booking
                                                Cost
                                            </b>
                                        </p>
                                    )}
                                </>
                            ) : quote.poa === true ? (
                                <p>
                                    Booking Cost: <b>{currencySymbol}POA</b>
                                </p>
                            ) : (
                                <>
                                    <p>
                                        Club RRP:{" "}
                                        <b>
                                            {currencySymbol}
                                            {Number(
                                                quote.originalTotal,
                                            ).toFixed(2)}
                                        </b>
                                    </p>
                                    <p>
                                        Booking Cost:{" "}
                                        <b>
                                            {currencySymbol}
                                            {Number(
                                                quote.discountedTotal,
                                            ).toFixed(2)}
                                        </b>{" "}
                                        (
                                        {Number(
                                            quote.discountPercentage,
                                        ).toFixed(0)}
                                        % off)
                                    </p>
                                </>
                            )}
                        </Segment>
                    ) : null}

                    {errors && (
                        <Message negative>
                            <Message.Header>
                                Unable to save booking
                            </Message.Header>
                            <Message.List>
                                {Object.values(errors)
                                    .flat()
                                    .map((msg, idx) => (
                                        <Message.Item key={idx}>
                                            {msg}
                                        </Message.Item>
                                    ))}
                            </Message.List>
                        </Message>
                    )}

                    <Segment vertical className='text-right'>
                        <Button
                            primary
                            className='theme'
                            loading={saving}
                            onClick={save}
                            disabled={!canSave}
                        >
                            Save
                        </Button>
                    </Segment>
                </>
            )}
        </>
    );
};

/**
 * CREDIT FLOW
 * - Member + balance, Club (+ filter), Course (credit-only), Players, Calendar (credit), Price block rules
 * - Removes "credit price for selected member" display (per your spec)
 * - Keeps creditPrice as "override booking cost" only when override is enabled (calendar must expose that state)
 */
const CreditFlow = ({ history, member }) => {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState(null);

    const [club, setClub] = useState(null);
    const [course, setCourse] = useState(null);
    const [clubCourses, setClubCourses] = useState([]);
    const [memberAccessClubsFilter, setMemberAccessClubsFilter] =
        useState(true);

    const [clubRetreived, setClubRetreived] = useState(null);

    const creditMin = clubRetreived?.bookingCreditMinPlayers ?? 2;
    const creditMax = clubRetreived?.bookingCreditMaxPlayers ?? 4;

    const [players, setPlayers] = useState(4);
    const [playersDraft, setPlayersDraft] = useState(String(4));

    const playersMin = creditMin;
    const playersMax = creditMax;

    const [hasSelectedDate, setHasSelectedDate] = useState(false);
    const [hasSelectedTime, setHasSelectedTime] = useState(false);

    const clampPlayers = (nextMin, nextMax) => {
        setPlayers((p) => {
            const v = p ?? nextMin;
            const clamped = Math.min(nextMax, Math.max(nextMin, v));
            setPlayersDraft(String(clamped));
            return clamped;
        });
    };

    const commitPlayersDraft = () => {
        const n = parseInt(playersDraft, 10);
        if (Number.isNaN(n)) {
            setPlayersDraft(String(playersMin));
            setPlayers(playersMin);
            return;
        }
        const clamped = Math.min(playersMax, Math.max(playersMin, n));
        setPlayers(clamped);
        setPlayersDraft(String(clamped));
    };

    const effectivePlayers = useMemo(() => {
        const n = parseInt(playersDraft, 10);
        if (Number.isNaN(n)) return null;
        const clamped = Math.min(playersMax, Math.max(playersMin, n));
        // Only treat it as valid if the user has actually entered something numeric
        return clamped;
    }, [playersDraft, playersMin, playersMax]);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [timeslotHour, setTimeslotHour] = useState(null);

    const onChangeDate = (date) => {
        date.setHours(12);
        setSelectedDate(date);
        setHasSelectedDate(true);
    };

    const onChangeTime = (hour) => {
        setTimeslotHour(hour);
        setHasSelectedTime(hour !== null);
    };

    const getClubCourses = async (clubId) => {
        const params = { sort: "name", supportsCreditBookings: true };
        const clubCourses = await new GolfClub({ _id: clubId }).coursesIndex(
            params,
        );

        setClubCourses(clubCourses);

        if (clubCourses.length > 0) {
            setCourse(clubCourses[0]);
        } else {
            setCourse(null);
        }
    };

    const onChangeClub = async (value, retreived) => {
        setClubRetreived(retreived ?? null);
        setClub(value);
        await getClubCourses(value);

        const nextMin = retreived?.bookingCreditMinPlayers ?? 2;
        const nextMax = retreived?.bookingCreditMaxPlayers ?? 4;
        clampPlayers(nextMin, nextMax);
    };

    const onChangeCourse = (value) => {
        const selected = clubCourses.find(({ _id }) => _id === value);
        setCourse(selected ?? null);
    };

    // CREDIT pricing rules:
    // - If override availability is enabled (calendar internal), user must enter Booking Cost (creditPrice) > 0
    // - Otherwise quote endpoint returns discountedTotal + newTeeTimeBalance
    //
    // create.js cannot currently detect calendar override state (it lives inside CourseAvailabilityCalendar).
    // We keep the structure ready; you can wire it once the calendar exposes override state.
    const [overrideAvailability, setOverrideAvailability] = useState(false); // placeholder (not wired)
    const [creditPrice, setCreditPrice] = useState(0); // manual when overrideAvailability is true
    const [creditQuote, setCreditQuote] = useState(null);
    const currencySymbol = creditQuote?.currency?.symbol ?? "£";

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!member?._id || !course?._id) {
                setCreditQuote(null);
                return;
            }

            // If override is on, we do manual creditPrice instead of quote
            if (overrideAvailability === true) {
                setCreditQuote(null);
                return;
            }

            if (hasSelectedDate === false || hasSelectedTime === false) {
                setCreditQuote(null);
                return;
            }

            const day = selectedDate.getDate();
            const month = selectedDate.getMonth() + 1;
            const year = selectedDate.getFullYear();
            const hour = timeslotHour;

            if (effectivePlayers == null) {
                setCreditQuote(null); // or setCreditQuote(null)
                return;
            }

            try {
                const q = await GolfCourse.creditQuote(
                    course._id,
                    day,
                    month,
                    year,
                    hour,
                    effectivePlayers,
                    member._id,
                    null, // voucher
                    null, // booking
                );

                if (!cancelled) setCreditQuote(q);
            } catch (e) {
                if (!cancelled) setCreditQuote(null);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [
        member?._id,
        course?._id,
        selectedDate,
        timeslotHour,
        effectivePlayers,
        overrideAvailability,
        hasSelectedDate,
        hasSelectedTime,
    ]);

    const hasInsufficientBalance =
        overrideAvailability === false &&
        creditQuote?.newTeeTimeBalance != null &&
        creditQuote.newTeeTimeBalance < 0;

    const needsManualCost = overrideAvailability === true;
    const manualCostInvalid =
        needsManualCost && (!creditPrice || creditPrice <= 0);

    const manualNewBalance =
        needsManualCost && creditPrice != null
            ? Number(member?.teeTimeCreditBalance ?? 0) -
              Number(creditPrice ?? 0)
            : null;

    const manualInsufficientBalance =
        needsManualCost === true &&
        !manualCostInvalid &&
        manualNewBalance != null &&
        manualNewBalance < 0;

    const hasValidSelectedDate =
        selectedDate instanceof Date &&
        !Number.isNaN(selectedDate?.getTime?.());

    const hasValidTimeslot =
        timeslotHour !== null &&
        (overrideAvailability === true
            ? hasValidSelectedDate
            : hasSelectedDate === true && hasSelectedTime === true);

    const saveAllowed =
        hasValidTimeslot &&
        !hasInsufficientBalance &&
        !manualCostInvalid &&
        !manualInsufficientBalance;

    const save = async () => {
        setSaving(true);
        setErrors(null);

        const finalPlayers = effectivePlayers ?? playersMin;

        try {
            const booking = new TeeTimeBooking({
                user: member._id,
                choices: [{ course: course._id }],
                players: finalPlayers,
                date: selectedDate,
                isCreditBooking: true,
                timeslot: formatTimeslotHour(timeslotHour),
            });

            // If override availability, use manual Booking Cost. Otherwise backend should calculate.
            if (needsManualCost) {
                booking.price = creditPrice;
            }

            const response = await booking.store();
            history.push("/tee-time-requests/" + response._id);
        } catch (e) {
            const apiErrors = e?.response?.data?.errors ?? {
                general: ["Something went wrong saving this booking."],
            };

            setErrors(apiErrors);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Segment vertical>
                <label className='with-input'>Club</label>
                <GolfClubSearchSelect
                    value={club ?? null}
                    onChange={onChangeClub}
                    memberCanAccess={
                        memberAccessClubsFilter ? member._id : null
                    }
                    acceptsCredit={true}
                    retrieve={[
                        "bookingCashMinPlayers",
                        "bookingCashMaxPlayers",
                        "bookingCreditMinPlayers",
                        "bookingCreditMaxPlayers",
                    ]}
                />
                <Checkbox
                    toggle
                    label='Hide clubs the member cannot access'
                    checked={memberAccessClubsFilter}
                    className='ml-3'
                    onChange={() => {
                        setMemberAccessClubsFilter(
                            memberAccessClubsFilter === false,
                        );
                        setClub(null);
                        setCourse(null);
                        setClubRetreived(null);
                    }}
                />
            </Segment>

            {club && (
                <Segment vertical>
                    <label className='with-input'>Course</label>
                    <small
                        style={{
                            display: "block",
                            marginTop: 8,
                            opacity: 0.75,
                        }}
                    >
                        Only showing courses where Green Fee Credit is accepted.
                    </small>

                    <Select
                        search
                        value={course?._id ?? null}
                        onChange={(_, { value }) => onChangeCourse(value)}
                        options={clubCourses
                            .filter((c) => c.supportsCreditBookings === true)
                            .map((c, index) => ({
                                key: index,
                                text: c.name,
                                value: c._id,
                            }))}
                    />

                    {/* Per your spec: remove the "Green Fee Credit price for selected member" line entirely.
                        Pricing is generated later when selecting date/time (quote). */}
                </Segment>
            )}

            {course && (
                <>
                    <Segment vertical>
                        <label className='with-input'>No. of players</label>

                        <NumberInput
                            min={playersMin}
                            max={playersMax}
                            placeholder='No. of players'
                            value={playersDraft}
                            onChange={(value) => {
                                const v =
                                    value === null ||
                                    typeof value === "undefined"
                                        ? ""
                                        : String(value);
                                setPlayersDraft(v.replace(/[^\d]/g, ""));
                            }}
                            onBlur={commitPlayersDraft}
                        />

                        <PlayersNotice
                            bookingType='CREDIT'
                            isCreditBooking={true}
                            voucher={null}
                            playersMin={playersMin}
                            playersMax={playersMax}
                        />
                    </Segment>

                    <Segment vertical>
                        <label className='with-input'>Date & time</label>

                        <CourseAvailabilityCalendar
                            dateValue={selectedDate}
                            timeValue={timeslotHour} // ✅ numeric hour
                            member={member._id}
                            course={course._id}
                            voucher={null}
                            onChangeDate={onChangeDate}
                            onChangeTime={onChangeTime} // ✅ numeric hour
                            onOverrideChange={(next) => {
                                setOverrideAvailability(next);

                                if (next === true) {
                                    const d = new Date();
                                    d.setHours(12);
                                    setSelectedDate(d);

                                    setTimeslotHour(null);
                                    setHasSelectedDate(false);
                                    setHasSelectedTime(false);

                                    setCreditQuote(null);
                                    setCreditPrice(0);
                                }
                            }}
                            isCreditBooking={true}
                            showTimeslotSelection
                            defaultFirstAvailable
                        />

                        {/* NOTE:
                           overrideAvailability should be driven by the calendar override toggle.
                           Once CourseAvailabilityCalendar exposes override state (e.g. onOverrideChange),
                           setOverrideAvailability(...) here. */}
                    </Segment>

                    {needsManualCost === true || creditQuote ? (
                        <Segment vertical>
                            <label className='with-input'>
                                Override Booking Price
                            </label>

                            {needsManualCost ? (
                                <>
                                    <p>
                                        As you are overriding the date and time
                                        availability for this course, we are
                                        unable to calculate the pricing for this
                                        booking. Please enter the total price
                                        the member will need to pay into the
                                        box.
                                    </p>

                                    <label className='with-input'>
                                        Booking Cost Total
                                    </label>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <NumberInput
                                            min={0}
                                            placeholder='Booking Cost'
                                            value={creditPrice}
                                            onChange={(value) =>
                                                setCreditPrice(value)
                                            }
                                        />

                                        <span
                                            style={{
                                                opacity: 0.75,
                                                fontSize: "0.9em",
                                            }}
                                        >
                                            (This amount will be immediately
                                            deducted from the member&apos;s
                                            Green Fee Credit on creation of the
                                            booking)
                                        </span>
                                    </div>

                                    {manualNewBalance != null &&
                                        !manualCostInvalid && (
                                            <p>
                                                New GFC Balance:{" "}
                                                <b>
                                                    {currencySymbol}
                                                    {Number(
                                                        manualNewBalance,
                                                    ).toFixed(2)}
                                                </b>
                                            </p>
                                        )}
                                    {manualCostInvalid && (
                                        <p className='mt-2'>
                                            <b>
                                                You must enter a valid Booking
                                                Cost
                                            </b>
                                        </p>
                                    )}
                                    {manualInsufficientBalance && (
                                        <p className='mt-2'>
                                            <b>
                                                The member does not have enough
                                                Green Fee Credit to make this
                                                booking.
                                            </b>
                                        </p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p>
                                        Current GFC Balance:{" "}
                                        <b>
                                            {currencySymbol}
                                            {member.teeTimeCreditBalance}
                                        </b>
                                    </p>
                                    <p>
                                        Booking Cost:{" "}
                                        <b>
                                            {currencySymbol}
                                            {creditQuote.discountedTotal}
                                        </b>
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                opacity: 0.75,
                                            }}
                                        >
                                            (This amount will be immediately
                                            deducted from the member&apos;s
                                            Green Fee Credit on creation of the
                                            booking)
                                        </span>
                                    </p>
                                    <p>
                                        New GFC Balance:{" "}
                                        <b>
                                            {currencySymbol}
                                            {creditQuote.newTeeTimeBalance}
                                        </b>
                                    </p>

                                    {hasInsufficientBalance && (
                                        <p className='mt-2'>
                                            <b>
                                                The member does not have enough
                                                Green Fee Credit to make this
                                                booking.
                                            </b>
                                        </p>
                                    )}
                                </>
                            )}
                        </Segment>
                    ) : null}

                    {errors && (
                        <Message negative>
                            <Message.Header>
                                Unable to save booking
                            </Message.Header>
                            <Message.List>
                                {Object.values(errors)
                                    .flat()
                                    .map((msg, idx) => (
                                        <Message.Item key={idx}>
                                            {msg}
                                        </Message.Item>
                                    ))}
                            </Message.List>
                        </Message>
                    )}

                    {saveAllowed ? (
                        <Segment vertical className='text-right'>
                            <Button
                                primary
                                className='theme'
                                loading={saving}
                                onClick={save}
                            >
                                Save
                            </Button>
                        </Segment>
                    ) : null}
                </>
            )}
        </>
    );
};

/**
 * VOUCHER FLOW
 * - Member, Voucher select, Players locked to voucher.players (disabled), Calendar (voucher id + credit flag from voucher.contract),
 * - Price block: "Free", Save
 */
const VoucherFlow = ({ history, member }) => {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState(null);

    const [userVouchers, setUserVouchers] = useState([]);
    const [vouchersLoading, setVouchersLoading] = useState(false);
    const [voucher, setVoucher] = useState(null);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [timeslotHour, setTimeslotHour] = useState(null);

    const playersMin = voucher?.players ?? 0;
    const playersMax = voucher?.players ?? 0;

    const [playersDraft, setPlayersDraft] = useState("");

    const [voucherQuote, setVoucherQuote] = useState(null);
    const [overrideAvailability, setOverrideAvailability] = useState(false);

    const [hasSelectedDate, setHasSelectedDate] = useState(false);
    const [hasSelectedTime, setHasSelectedTime] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!member?._id) return;

            setVouchersLoading(true);

            try {
                const { data } = await CourseVoucher.userIndex(member._id, {
                    usable: true,
                });

                if (!cancelled)
                    setUserVouchers(Array.isArray(data) ? data : []);
            } finally {
                if (!cancelled) setVouchersLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [member?._id]);

    useEffect(() => {
        if (voucher?.players != null) {
            setPlayersDraft(String(voucher.players));
        } else {
            setPlayersDraft("");
        }
    }, [voucher?._id]);

    const onChangeDate = (date) => {
        date.setHours(12);
        setSelectedDate(date);
        setHasSelectedDate(true);
    };

    const onChangeTime = (hour) => {
        setTimeslotHour(hour);
        setHasSelectedTime(hour !== null);
    };

    const onSelectVoucher = (voucherId) => {
        const selected =
            userVouchers.find(({ _id }) => _id === voucherId) ?? null;
        setVoucher(selected);
        // Players are locked by voucher.players
    };

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!member?._id || !voucher?._id) {
                setVoucherQuote(null);
                return;
            }

            if (overrideAvailability === true) {
                setVoucherQuote(null);
                return;
            }

            if (hasSelectedDate === false || hasSelectedTime === false) {
                setVoucherQuote(null);
                return;
            }

            const day = selectedDate.getDate();
            const month = selectedDate.getMonth() + 1;
            const year = selectedDate.getFullYear();
            const hour = timeslotHour;
            const players = voucher.players;

            try {
                const q =
                    voucher.contract !== null
                        ? await GolfCourse.creditQuote(
                              voucher.course._id,
                              day,
                              month,
                              year,
                              hour,
                              players,
                              member._id,
                              voucher._id,
                              null,
                          )
                        : await GolfCourse.quote(
                              voucher.course._id,
                              day,
                              month,
                              year,
                              hour,
                              players,
                              member._id,
                              voucher._id,
                              null,
                          );

                if (!cancelled) setVoucherQuote(q);
            } catch (e) {
                if (!cancelled) setVoucherQuote(null);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [
        member?._id,
        voucher?._id,
        selectedDate,
        timeslotHour,
        overrideAvailability,
    ]);

    const save = async () => {
        setSaving(true);
        setErrors(null);

        try {
            const booking = new TeeTimeBooking({
                user: member._id,
                choices: [{ course: voucher.course._id }],
                players: voucher.players,
                date: selectedDate,
                isCreditBooking: voucher.contract !== null, // per your spec
                timeslot: formatTimeslotHour(timeslotHour),
            });

            booking.voucher = voucher._id;

            const response = await booking.store();
            history.push("/tee-time-requests/" + response._id);
        } catch (e) {
            const apiErrors = e?.response?.data?.errors ?? {
                general: ["Something went wrong saving this booking."],
            };

            setErrors(apiErrors);
        } finally {
            setSaving(false);
        }
    };

    const canSave =
        hasSelectedDate === true &&
        hasSelectedTime === true &&
        timeslotHour !== null;

    return (
        <>
            <Segment vertical>
                <label className='with-input'>Voucher</label>

                {vouchersLoading ? (
                    <p style={{ opacity: 0.75 }}>Loading vouchers…</p>
                ) : userVouchers.length === 0 ? (
                    <p>
                        <b>This user does not have any vouchers to use.</b>
                    </p>
                ) : (
                    <Select
                        value={voucher?._id ?? null}
                        options={userVouchers.map((item, index) => ({
                            key: index,
                            text: `${item.course.longName} (${format(
                                item.activatesAt,
                                "dd/MM/yyyy",
                            )} - ${format(item.expiresAt, "dd/MM/yyyy")})`,
                            value: item._id,
                        }))}
                        onChange={(_, { value }) => onSelectVoucher(value)}
                    />
                )}
            </Segment>

            {userVouchers.length > 0 && voucher && (
                <>
                    <Segment vertical>
                        <label className='with-input'>No. of players</label>

                        <NumberInput
                            min={playersMin}
                            max={playersMax}
                            placeholder='No. of players'
                            value={playersDraft}
                            disabled
                            onChange={() => {}}
                        />

                        <PlayersNotice
                            bookingType='VOUCHER'
                            isCreditBooking={voucher.contract !== null}
                            voucher={voucher}
                            playersMin={playersMin}
                            playersMax={playersMax}
                        />
                    </Segment>

                    <Segment vertical>
                        <label className='with-input'>Date & time</label>

                        <CourseAvailabilityCalendar
                            dateValue={selectedDate}
                            timeValue={timeslotHour} // ✅ numeric hour
                            member={member._id}
                            course={voucher.course._id}
                            voucher={voucher._id}
                            onChangeDate={onChangeDate}
                            onChangeTime={onChangeTime} // ✅ numeric hour
                            isCreditBooking={voucher.contract !== null}
                            showTimeslotSelection
                            defaultFirstAvailable
                            onOverrideChange={(next) => {
                                setOverrideAvailability(next);

                                if (next === true) {
                                    const d = new Date();
                                    d.setHours(12);
                                    setSelectedDate(d);

                                    setTimeslotHour(null);
                                    setHasSelectedDate(false);
                                    setHasSelectedTime(false);

                                    setVoucherQuote(null);
                                }
                            }}
                        />
                    </Segment>

                    <Segment vertical>
                        <label className='with-input'>Booking Price</label>
                        <p>
                            Booking Cost: <b>Free</b>
                        </p>
                    </Segment>

                    {errors && (
                        <Message negative>
                            <Message.Header>
                                Unable to save booking
                            </Message.Header>
                            <Message.List>
                                {Object.values(errors)
                                    .flat()
                                    .map((msg, idx) => (
                                        <Message.Item key={idx}>
                                            {msg}
                                        </Message.Item>
                                    ))}
                            </Message.List>
                        </Message>
                    )}

                    <Segment vertical className='text-right'>
                        <Button
                            primary
                            className='theme'
                            loading={saving}
                            onClick={save}
                            disabled={!canSave}
                        >
                            Save
                        </Button>
                    </Segment>
                </>
            )}
        </>
    );
};

const Create = ({ history }) => {
    const [bookingType, setBookingType] = useState("CASH");
    const [member, setMember] = useState(null);

    const onChangeMember = async (value) => {
        const m = await Member.show(value);
        setMember(m);
    };

    return (
        <>
            <div className='d-flex main'>
                <Header history={history} />

                <div className='container'>
                    <h2>Create Green Fee (Cash / Credit / Voucher) Request</h2>

                    <label className='with-input'>Booking type</label>
                    <Segment vertical>
                        <Select
                            value={bookingType}
                            onChange={(_, { value }) => setBookingType(value)}
                            options={[
                                { key: 0, value: "CASH", text: "Green Fee" },
                                {
                                    key: 1,
                                    value: "CREDIT",
                                    text: "Green Fee Credit",
                                },
                                { key: 2, value: "VOUCHER", text: "Voucher" },
                            ]}
                        />
                    </Segment>

                    <MemberBlock
                        member={member}
                        bookingType={bookingType}
                        onChangeMember={onChangeMember}
                    />

                    {/* Flow rendering: key forces unmount/remount when switching booking type or member */}
                    {member && bookingType === "CASH" && (
                        <CashFlow
                            key={`cash-${member._id}`}
                            history={history}
                            member={member}
                        />
                    )}

                    {member &&
                        bookingType === "CREDIT" &&
                        member.canAccessTeeTimeCredit !== false && (
                            <CreditFlow
                                key={`credit-${member._id}`}
                                history={history}
                                member={member}
                            />
                        )}

                    {member && bookingType === "VOUCHER" && (
                        <VoucherFlow
                            key={`voucher-${member._id}`}
                            history={history}
                            member={member}
                        />
                    )}
                </div>
            </div>

            <Footer />
        </>
    );
};

function mapStateToProps(state) {
    return {
        user: state.user,
        notification: state.notification,
        validation: state.validation,
    };
}

export default connect(mapStateToProps)(Create);
